/**
 * Smart Mode Classifier
 *
 * Classifies SQL statements for smart commit mode:
 * - DML (INSERT/UPDATE/DELETE/MERGE) → begin transaction
 * - Read-only (SELECT/EXPLAIN/SHOW) → auto-commit
 * - DDL (CREATE/ALTER/DROP/TRUNCATE) → auto-commit
 * - Unknown → auto-commit (conservative, no transaction)
 */

export type SmartModeClassification = 'dml' | 'read-only' | 'ddl' | 'unknown';

/**
 * DML keywords that should trigger a transaction in smart mode.
 */
const DML_KEYWORDS = new Set(['INSERT', 'UPDATE', 'DELETE', 'MERGE']);

/**
 * Read-only keywords that execute in auto-commit.
 */
const READ_ONLY_KEYWORDS = new Set([
  'SELECT', 'WITH', 'EXPLAIN', 'SHOW', 'PRAGMA', 'DESCRIBE', 'DESC',
  'SP_HELP', 'SP_HELPTEXT', 'SP_HELPINDEX', 'SP_COLUMNS', 'SP_TABLES',
]);

/**
 * DDL keywords that execute in auto-commit (no wrapping in transaction).
 */
const DDL_KEYWORDS = new Set([
  'CREATE', 'ALTER', 'DROP', 'TRUNCATE', 'GRANT', 'REVOKE',
]);

/**
 * Strip SQL comments (line and block) from a statement.
 */
function stripComments(sql: string): string {
  let result = '';
  let i = 0;
  while (i < sql.length) {
    if (sql[i] === '-' && sql[i + 1] === '-') {
      while (i < sql.length && sql[i] !== '\n') i++;
      if (i < sql.length) { result += sql[i]; i++; }
      continue;
    }
    if (sql[i] === '/' && sql[i + 1] === '*') {
      i += 2;
      while (i < sql.length - 1 && !(sql[i] === '*' && sql[i + 1] === '/')) i++;
      if (i < sql.length - 1) i += 2;
      result += ' ';
      continue;
    }
    result += sql[i];
    i++;
  }
  return result;
}

/**
 * Strip string literals from SQL to avoid false keyword matches.
 */
function stripStringLiterals(sql: string): string {
  let result = '';
  let inString: string | null = null;
  for (let i = 0; i < sql.length; i++) {
    const ch = sql[i];
    if (inString) {
      if (ch === inString) {
        if (sql[i + 1] === inString) { i++; } else { inString = null; result += ch; }
      }
      continue;
    }
    if (ch === "'" || ch === '"') { inString = ch; result += ch; continue; }
    result += ch;
  }
  return result;
}

/**
 * Extract the leading keyword from a single SQL statement.
 */
function extractLeadingKeyword(statement: string): string | null {
  const cleaned = stripStringLiterals(stripComments(statement)).trim().replace(/\s+/g, ' ');
  const tokens = cleaned.split(' ').filter(Boolean);
  if (tokens.length === 0) return null;
  return tokens[0].toUpperCase();
}

/**
 * Split SQL into semicolon-delimited statements (respecting string literals).
 */
// TODO(sql-grammar): migrate to DocumentStructure (src/features/sql-grammar/document)
// — replace this ad-hoc splitter with getDocumentStructure(doc, id).statements
// once the Statement Classification slice lands the typed routing layer.
function splitStatements(sql: string): string[] {
  const stmts: string[] = [];
  let current = '';
  let inString: string | null = null;
  for (let i = 0; i < sql.length; i++) {
    const ch = sql[i];
    if (inString) {
      current += ch;
      if (ch === inString) {
        if (sql[i + 1] === inString) { current += sql[++i]; } else { inString = null; }
      }
      continue;
    }
    if (ch === "'" || ch === '"') { inString = ch; current += ch; continue; }
    if (ch === ';') { if (current.trim()) stmts.push(current.trim()); current = ''; continue; }
    current += ch;
  }
  if (current.trim()) stmts.push(current.trim());
  return stmts;
}

/**
 * Classify a SQL batch for smart commit mode.
 *
 * If any statement in the batch is DML, the whole batch is classified as 'dml'.
 * DDL and read-only don't trigger transactions.
 */
export function classifyForSmartMode(sql: string): SmartModeClassification {
  if (!sql || !sql.trim()) return 'unknown';

  const statements = splitStatements(sql);
  if (statements.length === 0) return 'unknown';

  let hasDml = false;
  let hasDdl = false;
  let hasReadOnly = false;

  for (const stmt of statements) {
    const keyword = extractLeadingKeyword(stmt);
    if (!keyword) continue;

    // Handle WITH (CTE) - check if it leads to DML
    if (keyword === 'WITH') {
      const upper = stripStringLiterals(stripComments(stmt)).toUpperCase();
      const dmlAfterCte = ['INSERT', 'UPDATE', 'DELETE', 'MERGE'].some((kw) =>
        upper.includes(` ${kw} `) || upper.includes(`)${kw} `) || upper.includes(`) ${kw} `)
        || upper.endsWith(` ${kw}`) || upper.endsWith(`)${kw}`)
      );
      if (dmlAfterCte) { hasDml = true; } else { hasReadOnly = true; }
      continue;
    }

    if (DML_KEYWORDS.has(keyword)) { hasDml = true; }
    else if (DDL_KEYWORDS.has(keyword)) { hasDdl = true; }
    else if (READ_ONLY_KEYWORDS.has(keyword)) { hasReadOnly = true; }
  }

  // DML takes precedence (mixed batch = treat as DML)
  if (hasDml) return 'dml';
  if (hasDdl) return 'ddl';
  if (hasReadOnly) return 'read-only';
  return 'unknown';
}
